import { useFavoriteAssets } from "@zapier/client";

import {
  BetaDataTableCell as Cell,
  BetaDataTableContext,
  BetaDataTableRow,
  IconButton,
  Tooltip,
} from "@zapier/design-system";

import { useTranslations } from "@zapier/i18n";
import { AssetTypes } from "@zapier/utils";
import { useCallback, useContext, useState } from "react";
import { ButtonWrapper } from "../ButtonWrapper";
import { flattenChildren } from "../flattenChildren";
import { KebabMenu, KebabMenuItem } from "../KebabMenu";
import { useEmitFavoriteEvent, FavoriteOptions } from "../../../events";
import styles from "./Table.module.css";

interface RowProps {
  allowsSelection?: boolean; // whether or not to render a checkbox for the row
  assetName?: string; // human-readable name used in unique aria-labels for favorites button
  assetType?: AssetTypes;
  children: React.ReactNode;
  className?: string;
  shareMessage?: string; // custom share message for the tooltip
  disabledShareMessage?: string; // show a disabled share option with a tooltip
  hiddenSelection?: boolean; // render the column for a checkbox (folder and Zap tables)
  id: string; // unique identifier for the row
  isTrashed?: boolean; // whether the item is in trash
  kebabMenuLabel?: string; // visible hover tooltip for the kebab button
  kebabMenuAriaLabel?: string; // unique aria-label for the kebab button (assistive tech only)
  menuItems?: KebabMenuItem[];
  isMenuLoading?: boolean; // menu options are still resolving; show/announce a loading state
  kebabMenuWhiteSpace?: boolean; // show whitespace even if there are no menu items
  onClickShare?: () => void;
  onOpenMenu?: () => void;
  onOpenChange?: (isOpen: boolean) => void; // reports the kebab menu opening and closing
  showOptions?: boolean;
  showFavoriteButton?: boolean;
  showShareButton?: boolean;
}

const Row = ({
  allowsSelection,
  assetName,
  assetType,
  children,
  className,
  shareMessage,
  disabledShareMessage,
  hiddenSelection,
  id,
  isTrashed = false,
  kebabMenuLabel,
  kebabMenuAriaLabel,
  menuItems,
  isMenuLoading = false,
  onClickShare,
  onOpenMenu,
  onOpenChange,
  showOptions = true,
  kebabMenuWhiteSpace = false,
  showFavoriteButton: showFavoriteButtonProperty = true,
  showShareButton = true,
}: RowProps) => {
  const t = useTranslations("table-row");
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const {
    clearSelection,
    isChecked,
    selectedRows = [],
  } = useContext(BetaDataTableContext);

  const { isFavorite, toggleFavorite } = useFavoriteAssets();
  const { emit: emitFavoriteEvent } = useEmitFavoriteEvent();

  const showFavoriteButton =
    !!assetType && showFavoriteButtonProperty && !isTrashed;
  const isCurrentFavorite = showFavoriteButton && isFavorite(id, assetType);
  const handleToggleFavorite = () => {
    if (assetType) {
      emitFavoriteEvent({
        optionSelected: isCurrentFavorite
          ? FavoriteOptions.Remove
          : FavoriteOptions.Add,
        interactionRawText: assetType,
      });
      toggleFavorite(id, assetType);
    }
  };

  const finalKebabMenuLabel = kebabMenuLabel || t("menus.options");

  // Visible hover tooltip stays generic; the asset name only appears in the
  // aria-label so screen-reader/voice-control users can distinguish rows.
  const favoritesTooltip = isCurrentFavorite
    ? t("tooltips.remove-from-favorites")
    : t("tooltips.add-to-favorites");

  const favoritesAriaLabel = (() => {
    if (isCurrentFavorite) {
      return assetName
        ? t("tooltips.remove-from-favorites-named", { name: assetName })
        : t("tooltips.remove-from-favorites");
    }
    return assetName
      ? t("tooltips.add-to-favorites-named", { name: assetName })
      : t("tooltips.add-to-favorites");
  })();

  const assetTypeLabel =
    assetType === AssetTypes.TableView ? "view" : assetType;

  const onShare = () => {
    if (onClickShare) {
      onClickShare();
    }
  };

  const handleOpenMenu = useCallback(() => {
    if (selectedRows.length > 0) {
      clearSelection?.();
    }

    onOpenMenu?.();
  }, [clearSelection, selectedRows.length, onOpenMenu]);

  const shouldHandleOpenMenu = selectedRows.length > 0 || !!onOpenMenu;

  return (
    <BetaDataTableRow
      id={id}
      className={`${styles.tr} ${isChecked(id) ? styles.trChecked : ""} ${isMenuOpen ? styles.trMenuOpen : ""} ${className || ""}`}
      allowsSelection={allowsSelection}
      hiddenSelection={hiddenSelection}
    >
      {/*
        Normalize the caller's cells into a flat, gap-free list before the DS
        applies its per-column sticky props (which it does BY INDEX, matching the
        header columns). flattenChildren unwraps <Fragment>-grouped cells
        (folder-tree rows) and drops falsy conditionals ({cond && <Cell/>}), so
        body cells stay aligned 1:1 with the header and the trailing sticky
        Options column pins in the body too.
      */}
      {flattenChildren(children)}
      {showOptions && (
        <Cell
          className={`${styles.iconButtonCell} ${!!kebabMenuWhiteSpace && !menuItems?.length && styles.kebabMenuWhiteSpace} ${!!showShareButton && styles.hasShareButton} ${showFavoriteButton && styles.hasFavoriteButton}`}
        >
          <div>
            {!!onClickShare && (
              <Tooltip
                allowMultiline
                content={
                  disabledShareMessage ||
                  shareMessage ||
                  `Share ${assetTypeLabel}`
                }
              >
                <ButtonWrapper isDisabled={!!disabledShareMessage}>
                  <IconButton
                    aria-label={
                      disabledShareMessage || `Share ${assetTypeLabel}`
                    }
                    isDisabled={!!disabledShareMessage}
                    key={`share-${id}`}
                    name="personAdd"
                    onPress={onShare}
                    size="small"
                    variant="ghost"
                  />
                </ButtonWrapper>
              </Tooltip>
            )}
            {showFavoriteButton && (
              <Tooltip content={favoritesTooltip}>
                <IconButton
                  aria-label={favoritesAriaLabel}
                  key={`favorite-${id}`}
                  name={isCurrentFavorite ? "ratingStarFill" : "ratingStar"}
                  onPress={handleToggleFavorite}
                  size="small"
                  variant="ghost"
                />
              </Tooltip>
            )}
            {!!menuItems?.length && (
              <KebabMenu
                key={`kebab-${id}`}
                menuLabel={t("menus.options-label")}
                buttonLabel={finalKebabMenuLabel}
                buttonAriaLabel={kebabMenuAriaLabel}
                menuItems={menuItems}
                loadingState={{
                  isLoading: isMenuLoading,
                  label: t("menus.options-loading"),
                }}
                onOpenMenu={shouldHandleOpenMenu ? handleOpenMenu : undefined}
                onOpenChange={(isOpen) => {
                  setIsMenuOpen(isOpen);
                  onOpenChange?.(isOpen);
                }}
              />
            )}
          </div>
        </Cell>
      )}
    </BetaDataTableRow>
  );
};

export default Row;
